import type { SupportContact, SupportContactView } from '@api/msp/support-contact';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { getSupportContact, setSupportContact } from '../lib/api';
import { useScope } from '../scope/scope';
import { Button } from '../ui/Button';
import { Skeleton } from '../ui/misc';
import { useToast } from '../ui/toast';

const field = 'h-9 w-full rounded-lg border border-line bg-panel px-2.5 text-[13.5px] outline-none focus:border-brand/50';
const empty = (value: string) => value.trim() || null;

function ContactLines({ contact }: { contact: SupportContact }) {
  const lines = [contact.url, contact.phone && `Phone: ${contact.phone}`, contact.email && `Email: ${contact.email}`].filter(Boolean);
  if (!lines.length) return <p className="text-[13px] text-ink-2">No support contact set.</p>;
  return <div className="space-y-1 text-[13.5px]">{lines.map((line, i) => <p key={i} className="break-all">{line}</p>)}</div>;
}

function SupportContactForm({ mspId, initial }: { mspId: string; initial: SupportContactView }) {
  const [url, setUrl] = useState(initial.url ?? '');
  const [phone, setPhone] = useState(initial.phone ?? '');
  const [email, setEmail] = useState(initial.email ?? '');
  const client = useQueryClient();
  const toast = useToast();
  const key = ['scope', mspId, 'support-contact'];
  const save = useMutation({
    mutationFn: (contact: SupportContact) => setSupportContact(mspId, contact),
    onSuccess: view => { client.setQueryData<SupportContactView>(key, view); toast({ title: 'Support contact saved', body: 'It will appear in the device tray’s Get help on Windows devices running agent 0.7.1 or later.' }); },
    onError: () => toast({ tone: 'warning', title: 'The support contact wasn’t saved', body: 'Check the link (https only), phone and email, and try again. Only an MSP-wide owner can change it.' }),
  });
  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    save.mutate({ url: empty(url), phone: empty(phone), email: empty(email) });
  };
  return (
    <form onSubmit={submit} className="space-y-3">
      <label className="block text-[13px] text-ink-2">Support link (https)
        <input value={url} onChange={e => setUrl(e.target.value)} maxLength={300} type="url" placeholder="https://wittco.ai/support" className={`${field} mt-1`} /></label>
      <label className="block text-[13px] text-ink-2">Support phone
        <input value={phone} onChange={e => setPhone(e.target.value)} maxLength={40} placeholder="+1 555 010 4455" className={`${field} mt-1`} /></label>
      <label className="block text-[13px] text-ink-2">Support email
        <input value={email} onChange={e => setEmail(e.target.value)} maxLength={254} type="email" placeholder="help@wittco.ai" className={`${field} mt-1`} /></label>
      <div><Button type="submit" disabled={save.isPending}>{save.isPending ? 'Saving…' : 'Save support contact'}</Button></div>
    </form>
  );
}

/**
 * The MSP's support contact for end users (WRMM-122): the tray's Get help entry offers it on Windows devices running
 * agent 0.7.1 or later (WRMM-56); each support action needs its contact (Submit a ticket can instead use the service desk form when that is on). MSP-wide
 * owners set and clear it here; everyone else only sees it. Every change is in the audit trail.
 */
export function SupportContactSetting() {
  const { mspId } = useScope();
  const contact = useQuery({ queryKey: ['scope', mspId, 'support-contact'], queryFn: ({ signal }) => getSupportContact(mspId, signal) });
  if (contact.isPending) return <Skeleton className="h-24 w-full" />;
  if (contact.isError) return <p className="text-[13px] text-ink-3">The support contact couldn’t be loaded.</p>;
  const view = contact.data;
  if (!view.canChange) {
    return (
      <div className="space-y-1">
        <ContactLines contact={view} />
        <p className="text-[13px] text-ink-3">Only an MSP-wide owner can change this.</p>
      </div>
    );
  }
  return <SupportContactForm mspId={mspId} initial={view} />;
}
